import useSWR from 'swr';
import get from 'lodash.get';

import { fetcher } from '../../../../../lib/fetch/fetcher';
import sendSentryMessage from '../../../../../lib/sentry/helpers/sendSentryMessage';
import { Discount } from '../../../../../types/api/discounts/Discount';
import { useSelectedVariantContext } from '../../../context/TreatmentSelectorContext';
import { useCookieWatcher } from '../../../../../helpers/cookie';

/**
 * Gets discount code info from API.
 */
const getDiscountInfo = async (discountCode: string) => {
    try {
        // await new Promise((resolve) => setTimeout(resolve, 10000));

        const response = await fetcher(`${process.env.NEXT_PUBLIC_API_URL}/discounts/${discountCode}`);

        if (!response.ok) {
            throw new Error(`Server responded with ${response.status} status.`);
        }

        const json = await response.json();
        return await json.data as Discount;
    } catch (error: any) {
        const errorMessage = error?.message || 'No error message available.';

        sendSentryMessage('Invalid promo code found.', (scope) => {
            scope.setExtra('Error message', errorMessage);
            scope.setExtra('Promo code', discountCode || 'No promo code given.');
        });

        return null;
    }
}

const useGetNewUserDiscount = (selectedVariant: ReturnType<typeof useSelectedVariantContext>) => {
    const discountCode = get(selectedVariant, 'public.treatmentSelector.newUserDiscount') || '';

    const [authedBefore] = useCookieWatcher('authed_before');

    // Get the recently viewed treatments using useQuery.
    const {
        data,
        isLoading,
        isValidating,
    } = useSWR(
        `discount-${discountCode}`,
        (discountCode && !authedBefore) ? () => (
            getDiscountInfo(discountCode)
        ) : null,
        {
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
            refreshWhenHidden: false,
            refreshWhenOffline: false,
            revalidateIfStale: false,
            revalidateOnMount: true,
        }
    );

    return {
        isLoading: isLoading || isValidating,
        discount: data || null,
    }
};

export default useGetNewUserDiscount;
